import * as coreEntities from 'core/store/index';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import analyticsService from '../../../services/analyticsService';
import type { RadarAlert } from '../../../types/radar';

type UseAlertDetailDrawerProps = {
  alert: RadarAlert | null;
  onStatusChanged?: (annotationId: string, newStatus: string, reviewerName: string) => void;
};

type UseAlertDetailDrawerReturn = {
  handleDismiss: () => void;
  handleMarkAsAttended: () => void;
  updating: boolean;
};

function useAlertDetailDrawer({ alert, onStatusChanged }: UseAlertDetailDrawerProps): UseAlertDetailDrawerReturn {
  const currentUser = useSelector((state: any) => coreEntities.auth.selectors.getUser(state));
  const [updating, setUpdating] = useState<boolean>(false);

  const getCurrentUserName = useCallback((): string => {
    if (!currentUser) return '';
    return `${currentUser.name || ''} ${currentUser.lastname || ''}`.trim();
  }, [currentUser]);

  const handleMarkAsAttended = useCallback(async () => {
    if (!alert?.id) return;

    setUpdating(true);
    try {
      await analyticsService.updateAnnotationStatus(alert.id, { status: 'REVIEWED' });
      onStatusChanged?.(alert.id, 'REVIEWED', getCurrentUserName());
    } catch (err) {
      console.error('Failed to mark as attended:', err);
    } finally {
      setUpdating(false);
    }
  }, [alert?.id, onStatusChanged, getCurrentUserName]);

  const handleDismiss = useCallback(async () => {
    if (!alert?.id) return;

    setUpdating(true);
    try {
      await analyticsService.updateAnnotationStatus(alert.id, { status: 'DISMISSED' });
      onStatusChanged?.(alert.id, 'DISMISSED', getCurrentUserName());
    } catch (err) {
      console.error('Failed to dismiss:', err);
    } finally {
      setUpdating(false);
    }
  }, [alert?.id, onStatusChanged, getCurrentUserName]);

  return { handleDismiss, handleMarkAsAttended, updating };
}

export default useAlertDetailDrawer;
